/**
 * Centralized URL search params utilities for TanStack Router
 *
 * Provides type-safe helpers for parsing and serializing URL search params
 * with consistent default value handling across all routes.
 */

/**
 * Parse a boolean from URL search param.
 * Handles: 'true', 'false', '1', '0', true, false
 * Returns defaultValue if undefined or invalid
 */
export function parseBoolean(
  value: unknown,
  defaultValue: boolean
): boolean {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return defaultValue;
}

/**
 * Parse a string from URL search param.
 * Returns undefined for empty strings (to keep URL clean)
 */
export function parseString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
}

/**
 * Parse a number from URL search param.
 * Returns defaultValue if undefined, invalid, or NaN
 */
export function parseNumber(
  value: unknown,
  defaultValue?: number
): number | undefined {
  if (value === undefined || value === null) return defaultValue;
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return defaultValue;
  return num;
}

/**
 * Serialize a boolean for URL.
 * Only includes in URL if different from default (keeps URLs clean)
 */
export function serializeBoolean(
  value: boolean,
  defaultValue: boolean
): string | undefined {
  return value === defaultValue ? undefined : String(value);
}

/**
 * Build search params object, filtering out undefined values.
 * This keeps URLs clean by only including params with actual values.
 */
export function buildSearchParams<T extends Record<string, unknown>>(
  params: T
): Partial<T> {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      (result as Record<string, unknown>)[key] = value;
    }
  }
  return result;
}

/**
 * Common search param type definitions that can be reused across routes
 */
export interface CommonSearchParams {
  /** Text search filter */
  search?: string;
  /** Client ID filter */
  clientID?: string;
}

/**
 * Validate common search params from URL
 */
export function validateCommonSearch(
  search: Record<string, unknown>
): CommonSearchParams {
  return {
    search: parseString(search.search),
    clientID: parseString(search.clientID),
  };
}
